import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  X,
  UserPlus,
  Camera,
  User,
  Briefcase,
  Phone,
  Clock,
  Building2,
  Loader2,
  Sparkles,
  Shield,
} from 'lucide-react';
import DefaultImagePNG from '../../../../assets/images/DefaultProfilePic.png';
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks';
import { createNewMemberAPI, getAllMembers } from '../../../../Redux/ApiCalls/Dashboard/MembersAPI';
import { getAllRolesAPI, assignRoleAPI } from '../../../../Redux/ApiCalls/Dashboard/RoleApi';
import { clearFlagsMembers } from '../../../../Redux/Slice/Dashboard/MemberSlice';
import imageCompression from 'browser-image-compression';
import { showErrorToast } from '../../../atoms/Utlis/Toast';
import { usePermissions } from '../../../../hooks/usePermissions';
import { JOB_TYPES, EMPLOYMENT_TYPES } from './memberFormStyles';
import { Member } from '../../../../Data/member.dto';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMemberCreated?: (newMember: Member) => void;
}

const gradient =
  'linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)';

export const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  onClose,
  onMemberCreated,
}) => {
  const dispatch = useAppDispatch();
  const { branches, selectedBranch } = useAppSelector((state) => state.auth);
  const { roles, isRolesFetched } = useAppSelector((state) => state.role);

  const { hasAnyPermission, permissionsLoading } = usePermissions();
  const canAssignRole = hasAnyPermission(['role_management.read', 'role_management.update']);

  const [name, setName] = useState<string>('');
  const [jobType, setJobType] = useState<string>('');
  const [employmentType, setEmploymentType] = useState<string>('Full Time');
  const [countryCode, setCountryCode] = useState<string>('+91');
  const [contact, setContact] = useState<string>('');
  const [branchId, setBranchId] = useState<number | string>(selectedBranch?.id || '');
  const [assignedRoleId, setAssignedRoleId] = useState<number | string>('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && canAssignRole && !permissionsLoading && !isRolesFetched && roles.length === 0) {
      dispatch(getAllRolesAPI());
    }
  }, [isOpen, canAssignRole, permissionsLoading, isRolesFetched, roles.length, dispatch]);

  if (!isOpen) return null;

  const blobToFile = (blob: Blob, fileName: string): File =>
    new File([blob], fileName, { type: blob.type });

  const compressImage = async (file: File): Promise<Blob> => {
    return imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: false,
    });
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return;
    let file = event.target.files[0];
    if (file.size / 1024 / 1024 > 2) {
      file = blobToFile(await compressImage(file), file.name);
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setSelectedImage(file);
      setImagePreview(reader.result as string);
    };
  };

  const validatePhone = (phone: string): boolean => /^[0-9]{10}$/.test(phone);

  const resetForm = () => {
    setName('');
    setJobType('');
    setEmploymentType('Full Time');
    setCountryCode('+91');
    setContact('');
    setBranchId(selectedBranch?.id || '');
    setAssignedRoleId('');
    setSelectedImage(null);
    setImagePreview('');
    setShowErrors(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const isNameValid = name.trim().length >= 2;
    const isJobValid = jobType.trim().length > 0;
    const isPhoneValid = validatePhone(contact);
    const isCountryValid = countryCode.trim().length > 0 && countryCode.trim().length <= 4;

    if (!isNameValid || !isJobValid || !isPhoneValid || !isCountryValid) {
      setShowErrors(true);
      if (!isNameValid) showErrorToast('Please enter member name (at least 2 characters)');
      else if (!isJobValid) showErrorToast('Please select a job role');
      else if (!isPhoneValid) showErrorToast('Please enter a valid 10-digit mobile number');
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('country_code', countryCode.trim());
      formData.append('phone_number', contact.trim());
      formData.append('job_type', jobType);
      formData.append('employment_type', employmentType);
      formData.append('name', name.trim());
      if (branchId) {
        formData.append('branch_id', String(branchId));
      }

      if (selectedImage) {
        formData.append('profile_image', selectedImage);
      } else {
        const defaultImageBlob = await fetch(DefaultImagePNG).then((res) => res.blob());
        formData.append('profile_image', defaultImageBlob, 'default.png');
      }

      const res = await dispatch(createNewMemberAPI(formData)).unwrap();

      // Refresh members list so new member appears in state
      const refreshedMembers = await dispatch(getAllMembers(selectedBranch?.id)).unwrap();

      // Clear flags in member slice so other pages aren't affected
      dispatch(clearFlagsMembers());

      // Find the newly created member from the refreshed list
      let createdMember: Member | undefined;
      const returnedId = res?.id || res?.data?.id || res?.result?.id || res?.data?.data?.id;
      if (Array.isArray(refreshedMembers)) {
        createdMember = refreshedMembers.find(
          (m: any) =>
            (returnedId && String(m.id) === String(returnedId)) ||
            m.phone_number === contact.trim()
        );
      }

      const memberIdToAssign = returnedId || createdMember?.id;
      if (canAssignRole && assignedRoleId && memberIdToAssign) {
        try {
          await dispatch(
            assignRoleAPI({
              roleId: assignedRoleId,
              teamMemberId: memberIdToAssign,
            })
          ).unwrap();
          if (canAssignRole) dispatch(getAllRolesAPI());
        } catch (roleErr: any) {
          console.error('Failed to assign role to new member:', roleErr);
        }
      }

      if (onMemberCreated && createdMember) {
        onMemberCreated(createdMember);
      }

      resetForm();
      onClose();
    } catch (err: any) {
      // In case error wasn't handled by slice
      const message = err?.message || err?.detail || 'Failed to create team member';
      showErrorToast(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderLeft>
            <HeaderIconWrap>
              <UserPlus size={18} color="#c94daa" />
            </HeaderIconWrap>
            <div>
              <ModalTitle>Add Team Member</ModalTitle>
              <ModalSubtitle>Create a new member profile for your studio</ModalSubtitle>
            </div>
          </HeaderLeft>
          <CloseBtn type="button" onClick={handleClose}>
            <X size={18} />
          </CloseBtn>
        </ModalHeader>

        <ModalForm onSubmit={handleSubmit}>
          <ModalContent>
            {/* Profile Photo Upload */}
            <PhotoSection>
              <PhotoUploadCircle onClick={() => fileInputRef.current?.click()} type="button">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                />
                <PhotoImage src={imagePreview || DefaultImagePNG} alt="Member preview" />
                <PhotoBadge>
                  <Camera size={14} color="#ffffff" />
                </PhotoBadge>
              </PhotoUploadCircle>
              <PhotoTextWrap>
                <PhotoTitle>Profile Photo</PhotoTitle>
                <PhotoSubtext>Optional · Click avatar to upload JPG or PNG (max 2 MB)</PhotoSubtext>
              </PhotoTextWrap>
            </PhotoSection>

            {/* Full Name */}
            <FieldGroup>
              <FieldLabel>
                <User size={13} color="#c94daa" />
                <span>Full Name</span>
                <Required>*</Required>
              </FieldLabel>
              <TextInput
                type="text"
                placeholder="e.g. Rahul Sharma"
                value={name}
                maxLength={25}
                onChange={(e) => setName(e.target.value)}
                $hasError={showErrors && name.trim().length < 2}
              />
              {showErrors && name.trim().length < 2 && (
                <ErrorText>Full name must be at least 2 characters</ErrorText>
              )}
            </FieldGroup>

            {/* Job Type & Employment Type */}
            <TwoColumns>
              <FieldGroup>
                <FieldLabel>
                  <Briefcase size={13} color="#c94daa" />
                  <span>Job Role</span>
                  <Required>*</Required>
                </FieldLabel>
                <SelectInput
                  value={jobType}
                  onChange={(e) => setJobType(e.target.value)}
                  $hasError={showErrors && !jobType}
                >
                  <option value="" disabled>
                    -- Select job role --
                  </option>
                  {JOB_TYPES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </SelectInput>
                {showErrors && !jobType && <ErrorText>Please choose a job role</ErrorText>}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>
                  <Clock size={13} color="#c94daa" />
                  <span>Employment</span>
                  <Required>*</Required>
                </FieldLabel>
                <SelectInput
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                >
                  {EMPLOYMENT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </SelectInput>
              </FieldGroup>
            </TwoColumns>

            {/* Studio Role / Dashboard Access (Optional) */}
            {canAssignRole && (
              <FieldGroup>
                <FieldLabel>
                  <Shield size={13} color="#c94daa" />
                  <span>Assign Studio Role</span>
                  <OptionalTag>(Optional)</OptionalTag>
                </FieldLabel>
                <SelectInput
                  value={assignedRoleId}
                  onChange={(e) => setAssignedRoleId(e.target.value)}
                >
                  <option value="">No custom role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({r.permissions?.length || 0} permissions)
                    </option>
                  ))}
                </SelectInput>
                {assignedRoleId && (
                  <RoleHintText>
                    <Sparkles size={12} color="#c94daa" />
                    <span>
                      {roles.find((r) => String(r.id) === String(assignedRoleId))?.description ||
                        "Auto-creates sub-user login access with assigned permissions."}
                    </span>
                  </RoleHintText>
                )}
              </FieldGroup>
            )}

            {/* Studio Branch (if available) */}
            {branches && branches.length > 0 && (
              <FieldGroup>
                <FieldLabel>
                  <Building2 size={13} color="#c94daa" />
                  <span>Studio Branch</span>
                </FieldLabel>
                <SelectInput
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  <option value="">All Branches / Studio HQ (No branch)</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.branch_name} ({b.branch_city})
                    </option>
                  ))}
                </SelectInput>
              </FieldGroup>
            )}

            {/* Phone Number & Country Code */}
            <FieldGroup>
              <FieldLabel>
                <Phone size={13} color="#c94daa" />
                <span>Mobile Number</span>
                <Required>*</Required>
              </FieldLabel>
              <PhoneInputRow>
                <CountryCodeBox>
                  <CountryInput
                    type="text"
                    value={countryCode}
                    maxLength={4}
                    onChange={(e) => setCountryCode(e.target.value)}
                    placeholder="+91"
                  />
                </CountryCodeBox>
                <PhoneInput
                  type="tel"
                  inputMode="numeric"
                  placeholder="10-digit mobile number"
                  value={contact}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    if (digits.length <= 10) setContact(digits);
                  }}
                  $hasError={showErrors && !validatePhone(contact)}
                />
              </PhoneInputRow>
              {showErrors && !validatePhone(contact) && (
                <ErrorText>Please enter a valid 10-digit mobile number</ErrorText>
              )}
            </FieldGroup>

            <NoticeBox>
              <Sparkles size={14} color="#c94daa" />
              <span>
                Once added, this member will immediately appear in your role assignment list.
              </span>
            </NoticeBox>
          </ModalContent>

          <ModalFooter>
            <SecondaryButton type="button" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </SecondaryButton>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Adding Member...</span>
                </>
              ) : (
                <>
                  <UserPlus size={15} />
                  <span>Add Member</span>
                </>
              )}
            </SubmitButton>
          </ModalFooter>
        </ModalForm>
      </ModalCard>
    </ModalOverlay>
  );
};

export default AddMemberModal;

/* Styled Components */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  animation: popIn 0.18s ease-out;

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  border-bottom: 1px solid #f0f0f0;
  background: #ffffff;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #171717;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 12px;
  color: #737373;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #737373;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #171717;
    background: #f5f5f5;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalContent = styled.div`
  padding: 20px 22px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: calc(90vh - 140px);
`;

const PhotoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  background: #fbfbfb;
  border: 1px dashed #e5e5e5;
  border-radius: 14px;
`;

const PhotoUploadCircle = styled.button`
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: 0 4px 12px rgba(201, 77, 170, 0.15);
  cursor: pointer;
  padding: 0;
  background: #f0f0f0;
  flex-shrink: 0;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.04);
  }
`;

const PhotoImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const PhotoBadge = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${gradient};
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PhotoTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PhotoTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #171717;
`;

const PhotoSubtext = styled.div`
  font-size: 11px;
  color: #737373;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #333333;
`;

const Required = styled.span`
  color: #ef4444;
  margin-left: -2px;
`;

const OptionalTag = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: #8c8c8c;
  margin-left: 2px;
`;

const RoleHintText = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #c94daa;
  margin-top: 2px;
`;

const TextInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.$hasError ? '#ef4444' : '#e5e5e5')};
  padding: 0 12px;
  font-size: 13px;
  outline: none;
  background: #ffffff;
  color: #171717;

  &:focus {
    border-color: ${(props) => (props.$hasError ? '#ef4444' : '#c94daa')};
    box-shadow: 0 0 0 2px rgba(201, 77, 170, 0.12);
  }
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SelectInput = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.$hasError ? '#ef4444' : '#e5e5e5')};
  padding: 0 10px;
  font-size: 13px;
  outline: none;
  background: #ffffff;
  color: #171717;

  &:focus {
    border-color: ${(props) => (props.$hasError ? '#ef4444' : '#c94daa')};
    box-shadow: 0 0 0 2px rgba(201, 77, 170, 0.12);
  }
`;

const PhoneInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountryCodeBox = styled.div`
  width: 72px;
  flex-shrink: 0;
`;

const CountryInput = styled.input`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  outline: none;
  color: #171717;

  &:focus {
    border-color: #c94daa;
    box-shadow: 0 0 0 2px rgba(201, 77, 170, 0.12);
  }
`;

const PhoneInput = styled.input<{ $hasError?: boolean }>`
  flex: 1;
  height: 40px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.$hasError ? '#ef4444' : '#e5e5e5')};
  padding: 0 12px;
  font-size: 13px;
  outline: none;
  color: #171717;

  &:focus {
    border-color: ${(props) => (props.$hasError ? '#ef4444' : '#c94daa')};
    box-shadow: 0 0 0 2px rgba(201, 77, 170, 0.12);
  }
`;

const ErrorText = styled.span`
  font-size: 11px;
  color: #ef4444;
  margin-top: 2px;
`;

const NoticeBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.2);
  border-radius: 10px;
  font-size: 12px;
  color: #831843;
  line-height: 1.4;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const SecondaryButton = styled.button`
  padding: 9px 18px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  color: #525252;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    color: #171717;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(201, 77, 170, 0.25);
  transition: all 0.15s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(201, 77, 170, 0.35);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
